// Where a share link lands: /s#<token>, or /s?to=now#<token> for a TV. The token is taken out
// of the address bar at once, sent to the server, which sets the share cookie, and the app
// opens. The owner opening their own link gets a preview instead.

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError } from "@/api";
import { Card } from "@/components/ui/card";
import { switchIdentity } from "@/lib/identity";
import { startPreview } from "@/lib/preview";

export function OpenSharePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<"opening" | "invalid" | "error">("opening");
  const started = useRef(false);

  useEffect(() => {
    // Once: the token is gone from the URL after the first run.
    if (started.current) return;
    started.current = true;
    const token = window.location.hash.slice(1);
    const to = new URLSearchParams(window.location.search).get("to") === "now" ? "/now" : "/day";
    window.history.replaceState(null, "", "/s");
    if (!token) {
      setState("invalid");
      return;
    }
    api.shares.open(token).then(
      async (opened) => {
        if (opened.role === "owner") startPreview(opened.shareId);
        await switchIdentity(queryClient);
        navigate(to, { replace: true });
      },
      (err: unknown) => setState(err instanceof ApiError && err.status === 404 ? "invalid" : "error"),
    );
  }, [navigate, queryClient]);

  return (
    <div className="grid min-h-screen place-items-center p-4">
      <Card className="w-full max-w-sm p-6 text-center">
        <img src="/favicon.svg" alt="" className="mx-auto size-10" />
        {state === "opening" && <p className="mt-4 text-sm text-muted">Opening the shared link…</p>}
        {state === "invalid" && (
          <>
            <h1 className="mt-4 font-semibold">This link doesn't work anymore</h1>
            <p className="mt-2 text-sm text-muted">It may have expired or been revoked. Ask the person who shared it for a new one.</p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="mt-4 font-semibold">Couldn't open the link</h1>
            <p className="mt-2 text-sm text-muted">The server didn't answer. Check the connection and open the link again.</p>
          </>
        )}
      </Card>
    </div>
  );
}
